// src/redux/slices/restaurantsSlice.js
import { getBranchDetails } from "../../../api/get/getBranchDetails";

import { getItems } from "../../../api/get/getItems";
import { getBranchCategories } from "../../../api/get/getCategories";
import { getBrandsByArea } from "../../../api/post/getBrandsByArea";
import { getRestaurantBranches } from "../../../api/get/getPickupbranches";
import {
  clearCart,
  setBrands,
  setCateringBrands,
  setCartId,
  setCategories,
  setDeliveryCharges,
  setItems,
  setPickupBrands,
  setProductDetail,
  setRestaurantBranches,
  setSelectedBranchDetails,
  setShowProductDetail,
  setUnavailableItems,
} from "./restaurantSlice";
import { addToCart } from "../../../api/post/addToCart";
import { getItemDetails } from "../../../api/get/getItemDetails";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { checkItemAvailability } from "../../../api/post/checkItemAvailability";
import { emptyCart } from "../../../api/post/emptyCart";
import { deleteCateringCart } from "../../../api/post/deleteCateringCart";
import { getAllRestaurants } from "../../../api/get/getAllRestaurants";
import { setCateringPackages, setCateringRestaurantId, setCateringCategories, setCateringItems } from "./restaurantSlice";
import { getRestaurantItemsCategories } from "../../../api/get/getRestaurantItemsCategories";
import { getCateringPackages } from "../../../api/get/getCateringPackages";
import { getCateringPackageDetail } from "../../../api/get/getCateringPackageDetail";
import { setPastOrderDetails } from "../order/orderSlice";

// Thunk to request support delivery by area
export const getBrandsByAreaAsync = (areaData) => async (dispatch) => {
  try {
    const response = await getBrandsByArea(
      areaData.areaName,
      areaData.serviceType
    );
    dispatch(setBrands(response));
  } catch (error) {
    console.error("Get Brands By Area request failed:", error);
  }
};

export const getAllPickupRestaurantsAsync = () => async (dispatch) => {
  try {
    const response = await getAllRestaurants();
    // Filter restaurants to only include those with pickup_status = true
    const pickupRestaurants = response.filter(
      (restaurant) => restaurant.pickup_status === true
    );
    dispatch(setPickupBrands(pickupRestaurants));
    return pickupRestaurants;
  } catch (error) {
    console.error("Get All Restaurants request failed:", error);
    throw error;
  }
};

export const getAllCateringRestaurantsAsync = () => async (dispatch) => {
  try {
    const response = await getAllRestaurants();
    // Filter restaurants to only include those with catering_status = true
    const cateringRestaurants = response.filter(
      (restaurant) => restaurant.catering_status === true
    );
    dispatch(setCateringBrands(cateringRestaurants));
    return cateringRestaurants;
  } catch (error) {
    console.error("Get All Catering Restaurants request failed:", error);
    throw error;
  }
};

export const getBranchDetailsAsync =
  (restaurantId, areaName, branchName) => async (dispatch, getState) => {
    try {
      const response = await getBranchDetails(restaurantId, areaName);

      // Check if response is empty, null, or doesn't have required data
      if (!response || Object.keys(response).length === 0) {
        console.log("Restaurant does not deliver to the selected address");
        // Return a resolved promise with a specific value to indicate no delivery
        return { success: false, reason: "no_delivery" };
      }

      // Get the current service type from the state
      const state = getState();
      const showProductDetail = state.restaurants.showProductDetail;
      showProductDetail && dispatch(setShowProductDetail(false));

      // Create payload
      const payload = {
        ...response,
        restaurantName: branchName,
      };

      // Set branch details
      dispatch(setSelectedBranchDetails(payload));

      // We'll let the PlaceOrder component handle clearing past order details
      // after processing to avoid timing issues

      // Return success result for components to use
      return { success: true, data: payload };
    } catch (error) {
      console.error("Get Branch Details request failed:", error);
      // Return error result
      return { success: false, reason: "error", error };
    }
  };

export const getCategoriesAsync =
  (restaurantId, branchId) => async (dispatch) => {
    try {
      const response = await getBranchCategories(restaurantId, branchId);
      dispatch(setCategories(response));
    } catch (error) {
      console.error("Get Categories request failed:", error);
    }
  };

export const getItemsAsync =
  (restaurantId, branchId, setLoading) => async (dispatch) => {
    try {
      const response = await getItems(restaurantId, branchId);
      dispatch(setItems(response));
    } catch (error) {
      console.error("Get Items request failed:", error);
    } finally {
      setLoading(false); // Stop loading once the fetch is complete (success or failure)
    }
  };

export const getItemDetailsAsync =
  (restaurantId, branchId, itemId, isReorder = false) =>
  async (dispatch) => {
    try {
      const response = await getItemDetails(restaurantId, branchId, itemId);
      dispatch(setProductDetail(response)); // Store item details in Redux
      if (isReorder) {
        dispatch(setShowProductDetail(false));
      } else {
        dispatch(setShowProductDetail(true)); // Show product details UI
      }
    } catch (error) {
      console.error("Get Item Details request failed:", error);
    }
  };

export const addToCartAsync = (cartData) => async (dispatch) => {
  try {
    const response = await addToCart(cartData);
    dispatch(setCartId(response)); // Update the cart state
  } catch (error) {
    console.error("Add to Cart request failed:", error);
  }
};

export const checkItemAvailabilityAsync = createAsyncThunk(
  "restaurants/checkItemAvailability",
  async (data, { dispatch }) => {
    try {
      const response = await checkItemAvailability(data);
      dispatch(setUnavailableItems(response.unavailable_item_ids || []));
      return response;
    } catch (error) {
      console.error("Error checking item availability:", error);
      throw error;
    }
  }
);

export const emptyCartAsync = createAsyncThunk(
  "restaurants/emptyCart",
  async (customerId, { dispatch }) => {
    try {
      await emptyCart(customerId);
    } catch (error) {
      console.error("Error emptying cart:", error);
      throw error;
    }
  }
);

export const emptyCateringCartAsync = createAsyncThunk(
  "restaurants/emptyCateringCart",
  async (cartData, { dispatch }) => {
    try {
      await deleteCateringCart(cartData);
    } catch (error) {
      console.error("Error emptying catering cart:", error);
      throw error;
    }
  }
);

/**
 * Thunk to fetch branches for a specific restaurant
 * @param {number} restaurantId - The ID of the restaurant
 * @returns {Promise} - A promise that resolves to the restaurant branches
 */
export const getRestaurantBranchesAsync =
  (restaurantId) => async (dispatch) => {
    try {
      const response = await getRestaurantBranches(restaurantId);
      // Filter branches to only include those with pickup_status = true
      const pickupBranches = response.filter(
        (branch) => branch.pickup_status === true
      );
      dispatch(setRestaurantBranches(pickupBranches));
      return pickupBranches;
    } catch (error) {
      console.error("Get Restaurant Branches request failed:", error);
      throw error;
    }
  };

export const setDeliveryChargesThunk =
  (serviceType) => (dispatch, getState) => {
    const state = getState();
    const deliveryCharges =
      state.restaurants.selectedBranchDetails?.delivery_charges;

    if (serviceType === "delivery") {
      dispatch(setDeliveryCharges(deliveryCharges));
    } else {
      dispatch(setDeliveryCharges("KWD 0.000"));
    }
  };

export const getPickupBranchDetails = (branchId) => (dispatch, getState) => {
  try {
    const state = getState();
    const restaurantBranches = state.restaurants.restaurantBranches;

    // Find the branch in the existing branches array
    const branch = restaurantBranches.find((branch) => branch.id === branchId);

    const showProductDetail = state.restaurants.showProductDetail;
    showProductDetail && dispatch(setShowProductDetail(false));

    if (!branch) {
      console.log("Branch not found in restaurant branches");
      return { success: false, reason: "branch_not_found" };
    }

    // Create payload similar to getBranchDetailsAsync
    const payload = {
      restaurant_id: branch.restaurant_id,
      branch_id: branch.id,
      name_en: branch.name_en,
      name_ar: branch.name_ar,
      delivery_charges: "KWD 0.000", // Always 0 for pickup
      minimum_order_amount: "",
      restaurantName: branch.name_en,
      branch_hours: branch.branch_hours,
    };

    // Set branch details
    dispatch(setSelectedBranchDetails(payload));

    return { success: true, data: payload };
  } catch (error) {
    console.error("Get Pickup Branch Details request failed:", error);
    return { success: false, reason: "error", error };
  }
};

export const getRestaurantItemsCategoriesAsync = (restaurantId, language = "en", cateringId = null, skipReduxUpdate = false) => async (dispatch) => {
  try {
    console.log(`Fetching categories for restaurant ${restaurantId} with language ${language}${cateringId ? ` and catering ID ${cateringId}` : ''}`);
    const response = await getRestaurantItemsCategories(restaurantId, language, cateringId);
    
    console.log("Categories API response:", response);
    console.log("Categories response type:", typeof response);
    console.log("Categories is array:", Array.isArray(response));
    
    // Only update Redux with main catering categories if not skipped (skip for additional services)
    if (!skipReduxUpdate && response && Array.isArray(response)) {
      console.log(`Storing ${response.length} categories in Redux`);
      dispatch(setCateringCategories(response));
      dispatch(setCateringRestaurantId(restaurantId)); // Track which restaurant this data belongs to
    } else if (skipReduxUpdate) {
      console.log(`Skipping Redux update for categories (additional service)`);
    } else {
      console.warn("Categories response is not an array or is empty:", response);
    }
    
    return response;
  } catch (error) {
    console.error("Get Restaurant Categories request failed:", error);
    throw error;
  }
};

export const getCateringPackagesAsync = (restaurantId, categoryId = "", language = "en", currencyId = 3) => async (dispatch) => {
  try {
    const response = await getCateringPackages(restaurantId, categoryId, language, currencyId);
    dispatch(setCateringPackages(response));
    dispatch(setCateringRestaurantId(restaurantId)); // Track which restaurant this data belongs to
    return response;
  } catch (error) {
    console.error("Get Catering Packages request failed:", error);
    throw error;
  }
};

export const getCateringPackageDetailAsync = (restaurantId, packageId, categoryId = "", language = "en", currencyId = 3, skipReduxUpdate = false) => async (dispatch) => {
  try {
    const response = await getCateringPackageDetail(restaurantId, packageId, categoryId, language, currencyId);
    
    // Only update Redux with main catering items if not skipped (skip for additional services)
    if (!skipReduxUpdate && response && response.categories && Array.isArray(response.categories)) {
      // Flatten all catering_service_item from all categories into a single items array
      // and add category information to each item
      const allItems = response.categories.flatMap(category => 
        (category.catering_service_item || []).map(item => ({
          ...item,
          category_id: category.id,
          category_name_en: category.name_en,
          category_name_ar: category.name_ar
        }))
      );
      dispatch(setCateringItems(allItems));
      dispatch(setCateringRestaurantId(restaurantId)); // Track which restaurant this data belongs to
    }
    
    return response;
  } catch (error) {
    console.error("Get Catering Package Detail request failed:", error);
    throw error;
  }
};
